import React, { useContext, useEffect, useState } from "react";
import { StyleDesignations } from "../../../common/interfaces";
import { useURLParameter } from "./URLParametersProvider";
import { useStyle } from "./StyleProvider";
import { notFalsy } from "../../../common/util";

export interface DesignationProviderValue {
  allAvailableDesignations: StyleDesignations[];
  isValid: () => boolean;
  designation: StyleDesignations;
  errors: {
    size?: string;
  };
  productCode: string | undefined;
  setDesignation: (designation: StyleDesignations) => void;
  setProductCode: (productCode: string | undefined) => void;
  size: string | undefined;
  setSize: (size: string | undefined) => void;
}

export const designationContextDefaults: DesignationProviderValue = {
  allAvailableDesignations: [StyleDesignations.RENTAL],
  isValid: () => false,
  productCode: undefined,
  designation: StyleDesignations.RENTAL,
  errors: {},
  setDesignation: () => {},
  setProductCode: () => {},
  size: undefined,
  setSize: () => {},
};

export const DesignationContext = React.createContext<DesignationProviderValue>(designationContextDefaults);

export const DesignationProvider: React.FC = ({ children }) => {
  const { buyInformation, subscriptionInformation, rentalInformation } = useStyle();
  const { designation: designationFromURL, productCode: productFromURL, size: sizeFromUrl } = useURLParameter();

  const [productCode, setProductCode] = useState<DesignationProviderValue["productCode"]>(productFromURL);
  const [size, setSize] = useState<DesignationProviderValue["size"]>(sizeFromUrl);
  const [errors, setErrors] = useState<DesignationProviderValue["errors"]>({});

  const hasRental = rentalInformation && rentalInformation.sizes.length !== 0;
  const hasBuy = buyInformation && buyInformation.sizes.length !== 0 && !!buyInformation.prices.buyPriceInCents;
  const hasSubscription = subscriptionInformation && subscriptionInformation.sizes.length !== 0;

  const allAvailableDesignations: StyleDesignations[] = [
    hasRental && StyleDesignations.RENTAL,
    hasSubscription && StyleDesignations.SUBSCRIPTION,
    hasBuy && StyleDesignations.BUY,
  ].filter(notFalsy);

  // Set to first available, fallback to Rental, URL will always override
  const [designation, setDesignation] = useState<StyleDesignations>(
    designationFromURL || allAvailableDesignations[0] || StyleDesignations.RENTAL
  );

  useEffect(() => {
    setErrors((errors) => ({ ...errors, size: undefined }));
  }, [productCode]);

  const isValid = () => {
    let isValid = true;
    if (!productCode) {
      isValid = false;
      setErrors((errors) => ({ ...errors, size: "Required" }));
    }

    return isValid;
  };

  const contextValue: DesignationProviderValue = {
    allAvailableDesignations,
    designation,
    errors,
    isValid,
    productCode,
    setDesignation,
    setProductCode,
    size,
    setSize,
  };

  return <DesignationContext.Provider value={contextValue}>{children}</DesignationContext.Provider>;
};

export const useDesignations = () => useContext(DesignationContext);
